import React, { useMemo } from 'react';

import { Redirect, Route, Switch } from 'react-router-dom';
import { Box, LinearProgress } from '@material-ui/core';

import { CommonNavBarBlock } from '@vyce/core/src/components';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';

import { FORMS_TABS } from '@vyce/core/src/views/forms/constants';
import { useStyles } from './styles';
import { MyForms } from '@vyce/core/src/views/forms/MyForms';
import { FormsToAnswer } from '@vyce/core/src/views/forms/FormsToAnswer';
import { SharedWithMeForms } from '@vyce/core/src/views/forms/SharedWithMeForms';
import { FormConstructor } from '@vyce/core/src/views/forms/FormConstructor';
import { GetStarted } from './components/GetStarted';
import { ExistingForm } from '@vyce/core/src/views/forms/ExistingForm';
import { Form } from '@vyce/core/src/views/forms/Form';
import { FormAnswers } from '@vyce/core/src/views/forms/FormAnswers';
import { useTypedSelector } from 'src/hooks';
import { INTERFACE_PERMISSIONS } from '@vyce/core/src/types';
import { FormTemplates } from '@vyce/core/src/views/forms/FormTemplates';
import { ExistingFormTemplate } from '@vyce/core/src/views/forms/ExistingFormTemplate';
import { FolderFormsPage } from '@vyce/core/src/views/forms/FolderFormsPage';
import { TemplateFolderPage } from '@vyce/core/src/views/forms/TemplateFolderPage';

export const EmployerForms: React.FC = () => {
  const classes = useStyles();
  const { user } = useTypedSelector(state => state);
  const { can } = usePermissions();
  const { selectedCompanyAppData, selectedCompany } = useTypedSelector(state => state.helper);

  const locations = useMemo(() => {
    return selectedCompanyAppData?.locations || [];
  }, [selectedCompanyAppData?.locations]);

  const teams = useMemo(() => {
    return selectedCompanyAppData?.teams || [];
  }, [selectedCompanyAppData?.teams]);

  const hadSubscription =
    (!!selectedCompanyAppData?.subscription?.uuid && !selectedCompanyAppData.subscription.active) || false;
  const hasBillingPermissions = can(INTERFACE_PERMISSIONS.BILLING);
  const selectedCompanyIsSubscribed = selectedCompanyAppData?.subscription?.active ?? false;

  if (selectedCompanyAppData?.companyDataLoading) {
    return <LinearProgress />;
  }

  if (!selectedCompanyAppData?.formsSubscription || !selectedCompany?.uuid) {
    return (
      <GetStarted
        hadSubscription={hadSubscription}
        hasPermissions={hasBillingPermissions}
        hasSubscription={selectedCompanyIsSubscribed}
      />
    );
  }

  return (
    <Box height="100%">
      <Box className={classes.moduleContainer}>
        <CommonNavBarBlock tabItems={FORMS_TABS} />

        <Box display="flex" flexDirection="column" className={classes.container}>
          <Switch>
            <Route
              path="/forms/my-forms/folders/:folderSlug+"
              render={() => <FolderFormsPage user={user} companyId={selectedCompany?.uuid ?? ''} />}
            />
            <Route
              path="/forms/my-forms/folders"
              render={() => <MyForms user={user} companyId={selectedCompany?.uuid ?? ''} />}
            />
            <Route
              path="/forms/my-forms/:id"
              render={() => (
                <ExistingForm
                  user={user}
                  companyId={selectedCompany?.uuid ?? ''}
                  locations={locations}
                  teams={teams}
                />
              )}
            />
            <Route
              path="/forms/my-forms"
              render={() => <MyForms user={user} companyId={selectedCompany?.uuid ?? ''} />}
            />
            <Route
              path="/forms/forms-to-answer"
              component={() => <FormsToAnswer user={user} companyId={selectedCompany?.uuid ?? ''} />}
            />
            <Route
              path="/forms/form-to-answer/:id"
              render={() => <Form user={user} companyId={selectedCompany?.uuid ?? ''} />}
            />
            <Route
              path="/forms/form-answers/:id"
              render={() => (
                <FormAnswers
                  user={user}
                  companyId={selectedCompany?.uuid ?? ''}
                  locations={locations}
                  teams={teams}
                  companyLogoUrl={selectedCompany?.logo?.url}
                />
              )}
            />
            <Route
              path="/forms/form-templates/folders/:folderSlug+"
              render={() => <TemplateFolderPage user={user} companyId={selectedCompany?.uuid ?? ''} />}
            />
            <Route
              path="/forms/form-templates/folders"
              render={() => <FormTemplates user={user} companyId={selectedCompany?.uuid ?? ''} />}
            />
            <Route
              path="/forms/form-templates/:templateId/edit"
              render={() => <ExistingFormTemplate user={user} companyId={selectedCompany?.uuid ?? ''} />}
            />
            <Route
              path="/forms/form-templates"
              render={() => <FormTemplates user={user} companyId={selectedCompany?.uuid ?? ''} />}
            />
            <Route
              path="/forms/form-templates/:templateId/edit"
              render={() => <ExistingFormTemplate user={user} companyId={selectedCompany?.uuid ?? ''} />}
            />
            <Route
              path="/forms/form-templates"
              render={() => <FormTemplates user={user} companyId={selectedCompany?.uuid ?? ''} />}
            />
            <Route
              path="/forms/new-template"
              render={() => (
                <FormConstructor
                  user={user}
                  companyId={selectedCompany?.uuid ?? ''}
                  locations={[]}
                  isTemplate
                />
              )}
            />
            {/* <Route path="/forms/shared-with-me" component={SharedWithMeForms} /> */}
            <Route
              path="/forms/new-form"
              render={({ location }) => (
                <FormConstructor
                  user={user}
                  companyId={selectedCompany?.uuid ?? ''}
                  locations={locations}
                  teams={teams}
                  initialFolderId={new URLSearchParams(location.search).get('folderId')}
                />
              )}
            />
            <Redirect from="/forms" to="/forms/my-forms" />
          </Switch>
        </Box>
      </Box>
    </Box>
  );
};
